// STEP 2: Editor righe misura per voce in modalita 'detailed'
// Calcolo Q.ta riga = prodotto dei campi dimensionali valorizzati (vuoti ignorati)
// Riusa evaluateFormula() di FormulaInput per parsing sicuro (no eval).

import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { evaluateFormula } from '../FormulaInput';

const DIM_FIELDS = ['parts', 'length', 'width', 'height_weight'];
const DIM_LABELS = {
  parts: 'Par. ug.',
  length: 'Lung.',
  width: 'Larg.',
  height_weight: 'H/Peso'
};

// Calcola Q.ta della singola riga
export const computeMeasurementLineQty = (line) => {
  const filled = DIM_FIELDS.filter(f => {
    const v = line?.[f];
    return v !== null && v !== undefined && String(v).trim() !== '';
  });
  if (filled.length > 0) {
    let result = 1;
    for (const f of filled) {
      const r = evaluateFormula(String(line[f]));
      if (r.error || isNaN(r.value) || r.value === null) return 0;
      result *= r.value;
    }
    return Math.round(result * 10000) / 10000;
  }
  // No dimensioni: usa manual_quantity
  if (line?.manual_quantity !== null && line?.manual_quantity !== undefined && line.manual_quantity !== '') {
    const parsed = parseFloat(String(line.manual_quantity).replace(',', '.'));
    return isNaN(parsed) ? 0 : Math.round(parsed * 10000) / 10000;
  }
  return 0;
};

// Calcola Q.ta totale voce dalla somma delle righe
export const computeTotalFromMeasurementLines = (lines = []) => {
  return Math.round(
    lines.reduce((sum, l) => sum + (l.calculated_quantity || 0), 0) * 10000
  ) / 10000;
};

const newEmptyLine = (sortOrder = 0) => ({
  id: `ml-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
  description: '',
  parts: '',
  length: '',
  width: '',
  height_weight: '',
  manual_quantity: null,
  calculated_quantity: 0,
  sort_order: sortOrder
});

const MeasurementLinesEditor = ({ lines = [], unit = '', onChange }) => {
  // Apri sempre per default (workflow piu fluido in fase di inserimento misure)
  const [expanded, setExpanded] = useState(true);

  const totalQty = computeTotalFromMeasurementLines(lines);
  const hasInvalidLines = lines.some(l => (l.calculated_quantity || 0) <= 0);

  const updateLine = (id, field, value) => {
    const next = lines.map(l => {
      if (l.id !== id) return l;
      const updated = { ...l, [field]: value };
      updated.calculated_quantity = computeMeasurementLineQty(updated);
      return updated;
    });
    onChange(next);
  };

  const addLine = () => {
    const next = [...lines, newEmptyLine(lines.length)];
    onChange(next);
    setExpanded(true);
  };

  const removeLine = (id) => {
    onChange(lines.filter(l => l.id !== id));
  };

  return (
    <div className="mt-2 border border-[#e5e7eb] rounded-md bg-white border-l-2 border-l-[#FF7819]" data-testid="measurement-lines-editor">
      {/* Header collassabile */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#fafafa] transition-colors"
        data-testid="measurement-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4 text-[#FF7819]" /> : <ChevronDown className="w-4 h-4 text-[#FF7819]" />}
          <span className="font-medium text-[#3C3C3C]">
            Misure / Computo quantità
          </span>
          <span className="text-xs text-[#737373]">
            {lines.length === 0 ? 'nessuna riga' : `${lines.length} ${lines.length === 1 ? 'riga' : 'righe'}`}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {hasInvalidLines && lines.length > 0 && (
            <AlertTriangle className="w-4 h-4 text-amber-500" title="Almeno una riga ha quantità zero" />
          )}
          <span className="text-[#737373]">Totale:</span>
          <span className="font-bold text-[#FF7819]">
            {totalQty.toString().replace('.', ',')}
            {unit && <span className="ml-1 text-xs text-[#737373]">{unit}</span>}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#e5e7eb] p-3 space-y-2">
          {lines.length === 0 ? (
            <div className="text-center text-sm text-[#737373] py-3">
              Nessuna riga misura. Aggiungi una riga per iniziare il computo.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#e5e7eb] bg-[#fafafa] text-[#737373]">
                    <th className="text-left py-1 px-1 font-medium">Descrizione</th>
                    <th className="text-center py-1 px-1 font-medium w-20">Par. ug.</th>
                    <th className="text-center py-1 px-1 font-medium w-20">Lung.</th>
                    <th className="text-center py-1 px-1 font-medium w-20">Larg.</th>
                    <th className="text-center py-1 px-1 font-medium w-20">H/Peso</th>
                    <th className="text-right py-1 px-1 font-medium w-20">Q.tà</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const lineInvalid = (line.calculated_quantity || 0) <= 0;
                    return (
                      <tr key={line.id} className="border-b border-[#f5f5f5]" data-testid={`measurement-line-${line.id}`}>
                        <td className="py-1 px-1">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                            placeholder="Es: Bagno 1"
                            className="h-7 text-xs"
                            data-testid={`ml-desc-${line.id}`}
                          />
                        </td>
                        {DIM_FIELDS.map((f) => (
                          <td key={f} className="py-1 px-1">
                            <Input
                              value={line[f] ?? ''}
                              onChange={(e) => updateLine(line.id, f, e.target.value)}
                              placeholder=""
                              className="h-7 text-xs text-center"
                              data-testid={`ml-${f}-${line.id}`}
                            />
                          </td>
                        ))}
                        <td className="py-1 px-1 text-right">
                          <span className={`font-semibold text-xs ${lineInvalid ? 'text-amber-600' : 'text-[#3C3C3C]'}`} data-testid={`ml-qty-${line.id}`}>
                            {(line.calculated_quantity || 0).toString().replace('.', ',')}
                          </span>
                        </td>
                        <td className="py-1 px-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLine(line.id)}
                            className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                            title="Elimina riga"
                            data-testid={`ml-remove-${line.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Riga somma */}
                  <tr className="bg-[#fafafa] border-t border-[#e5e7eb]">
                    <td colSpan={5} className="py-1 px-1 text-right text-xs font-semibold text-[#737373]">
                      SOMMANO
                    </td>
                    <td className="py-1 px-1 text-right">
                      <span className="font-bold text-sm text-[#FF7819]" data-testid="ml-total-qty">
                        {totalQty.toString().replace('.', ',')}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLine}
              className="border-[#e5e7eb] text-[#3C3C3C] hover:border-[#FF7819] hover:text-[#FF7819] hover:bg-[#fafafa] h-7 text-xs"
              data-testid="ml-add-line-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Aggiungi riga misura
            </Button>
            <p className="text-[10px] text-[#737373]">
              I campi accettano formule semplici (+, -, *, /, parentesi).
              Campi vuoti vengono ignorati nel calcolo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasurementLinesEditor;
